import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { Button } from "../button/index.js";
import { modalOverlayMotion, modalPanelMotion } from "./modal-motion.js";

/**
 * Centered dialog with a blurred backdrop. Focus returns to the control that opened it.
 * With an `icon`, the header uses the larger feature layout (icon well, bigger title).
 */
export function Modal({
  open,
  onOpenChange,
  onExitComplete,
  title,
  description,
  icon,
  children,
  testId,
  className,
  panelClassName,
  bodyClassName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExitComplete?: () => void;
  title: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  testId: string;
  /** Positions and sizes the fixed frame. */
  className?: string;
  panelClassName?: string;
  bodyClassName?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const overlay = modalOverlayMotion(reduceMotion);
  const panel = modalPanelMotion(reduceMotion);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  if (open && returnFocusRef.current == null) {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body) {
      returnFocusRef.current = active;
    }
  }
  const featured = icon != null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence onExitComplete={onExitComplete}>
        {open ? (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[var(--z-overlay)] bg-ink/35 backdrop-blur-md"
                variants={overlay}
                initial="initial"
                animate="animate"
                exit="exit"
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              forceMount
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                const target = returnFocusRef.current;
                returnFocusRef.current = null;
                target?.focus();
              }}
            >
              <div
                data-testid={testId}
                className={cn(
                  "fixed left-1/2 top-1/2 z-[var(--z-sheet)] w-[min(36rem,calc(100vw-1.5rem))] max-h-[min(90dvh,42rem)] -translate-x-1/2 -translate-y-1/2",
                  className,
                )}
              >
                <motion.div
                  className={cn(
                    "grid max-h-[inherit] w-full origin-center overflow-y-auto rounded-lg border border-line bg-surface text-foreground shadow-lg",
                    panelClassName,
                  )}
                  variants={panel}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <div
                    className={cn(
                      "flex items-start gap-3",
                      featured ? "px-[18px] pt-[18px] pb-3" : "justify-between border-b border-line/70 px-4 py-3",
                    )}
                  >
                    {featured ? (
                      <span
                        className="grid size-[38px] shrink-0 place-items-center rounded-[11px] bg-brand-mint text-primary"
                        aria-hidden="true"
                      >
                        {icon}
                      </span>
                    ) : null}
                    <div className="grid min-w-0 flex-1 gap-1 text-start">
                      <DialogPrimitive.Title
                        className={cn(
                          "m-0 text-foreground",
                          featured ? "text-[1.0625rem] font-[750] text-inbox-strong" : "text-[1rem] font-semibold",
                        )}
                      >
                        {title}
                      </DialogPrimitive.Title>
                      {description ? (
                        <DialogPrimitive.Description
                          className={cn("m-0 text-muted", featured ? "text-[0.78125rem]" : "text-[0.8125rem]")}
                        >
                          {description}
                        </DialogPrimitive.Description>
                      ) : (
                        <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
                      )}
                    </div>
                    <DialogPrimitive.Close asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
                        aria-label={t("common.close")}
                      >
                        <X className="size-4" aria-hidden="true" />
                      </Button>
                    </DialogPrimitive.Close>
                  </div>
                  <div className={cn("px-4 py-4", bodyClassName)}>{children}</div>
                </motion.div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        ) : null}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
